import type { FC } from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import type { TFunction } from 'i18next';
import type { Control, FieldErrors } from 'react-hook-form';
import { Controller, useWatch } from 'react-hook-form';
import { Flex, Form, Input, Radio } from 'antd';
import TextArea from 'antd/es/input/TextArea';
import classNames from 'classnames';

import type { UpdateFormValues } from '@modules/ai-agent/lib/types';
import { Text } from '@shared/ui/antd';

import styles from '../../prompt.module.scss';

import {
  buildRoleSegment,
  buildStyleSegment,
  getRoleMarker,
  getRoleMarkers,
  getStyleMarker,
  getStyleMarkers,
  getRoleNameOptions,
  parseRolePresetWithMarkers,
  parseStylePresetWithMarkers,
  ROLE_GENDER_OPTIONS,
  ROLE_GOAL_OPTIONS,
  ROLE_GROUP_LABEL_KEYS,
  ROLE_POSITION_OPTIONS,
  STYLE_ADDRESSING_OPTIONS,
  STYLE_EMOJI_OPTIONS,
  STYLE_GROUP_LABEL_KEYS,
  STYLE_LENGTH_OPTIONS,
  STYLE_TONE_OPTIONS,
  type RolePreset,
  type StylePreset,
} from './lib/presets';

interface BasicFieldsProps {
  control: Control<UpdateFormValues>;
  errors: FieldErrors<UpdateFormValues>;
  isLoading: boolean;
  t: TFunction<'translation', 'prompt'>;
}

export const BasicFields: FC<BasicFieldsProps> = ({ control, errors, isLoading, t }) => {
  const [rolePreset, setRolePreset] = useState<RolePreset>({});
  const [stylePreset, setStylePreset] = useState<StylePreset>({});
  const roleValue = useWatch({ control, name: 'role' });
  const styleValue = useWatch({ control, name: 'style' });
  const lastRoleValueRef = useRef<string | null>(null);
  const lastStyleValueRef = useRef<string | null>(null);

  const roleStep = useMemo(() => {
    if (!rolePreset.position) return 'position';
    if (!rolePreset.gender) return 'gender';
    if (!rolePreset.name) return 'name';
    if (!rolePreset.goal) return 'goal';
    return null;
  }, [rolePreset.gender, rolePreset.goal, rolePreset.name, rolePreset.position]);

  const styleStep = useMemo(() => {
    if (!stylePreset.addressing) return 'addressing';
    if (!stylePreset.tone) return 'tone';
    if (!stylePreset.emojis) return 'emojis';
    if (!stylePreset.length) return 'length';
    return null;
  }, [stylePreset.addressing, stylePreset.emojis, stylePreset.length, stylePreset.tone]);

  useEffect(() => {
    const nextRoleValue = roleValue ?? '';
    if (lastRoleValueRef.current === nextRoleValue) return;
    const nextRolePreset = nextRoleValue
      ? parseRolePresetWithMarkers(nextRoleValue, rolePreset)
      : {};

    if (
      nextRolePreset.position !== rolePreset.position ||
      nextRolePreset.gender !== rolePreset.gender ||
      nextRolePreset.name !== rolePreset.name ||
      nextRolePreset.goal !== rolePreset.goal
    ) {
      setRolePreset(nextRolePreset);
    }

    lastRoleValueRef.current = nextRoleValue;
  }, [rolePreset, roleValue]);

  useEffect(() => {
    const nextStyleValue = styleValue ?? '';
    if (lastStyleValueRef.current === nextStyleValue) return;
    const nextStylePreset = nextStyleValue
      ? parseStylePresetWithMarkers(nextStyleValue, stylePreset)
      : {};

    if (
      nextStylePreset.addressing !== stylePreset.addressing ||
      nextStylePreset.tone !== stylePreset.tone ||
      nextStylePreset.emojis !== stylePreset.emojis ||
      nextStylePreset.length !== stylePreset.length
    ) {
      setStylePreset(nextStylePreset);
    }

    lastStyleValueRef.current = nextStyleValue;
  }, [stylePreset, styleValue]);

  const applyPresetSegment = (
    nextSegment: string,
    marker: string,
    markers: string[],
    currentValue: string,
    onChange: (value: string) => void,
  ) => {
    if (!nextSegment) return;
    const value = currentValue ?? '';
    const markerIndex = value.indexOf(marker);

    const appendSegment = () => {
      const trimmed = value.trim();
      if (!trimmed) {
        onChange(nextSegment);
        return;
      }
      const separator = trimmed.endsWith(' ') || nextSegment.startsWith(' ') ? '' : ' ';
      onChange(`${trimmed}${separator}${nextSegment}`);
    };

    if (markerIndex === -1) {
      appendSegment();
      return;
    }

    let nextMarkerIndex = value.length;
    markers.forEach((nextMarker) => {
      if (nextMarker === marker) return;
      const index = value.indexOf(nextMarker, markerIndex + marker.length);
      if (index !== -1 && index < nextMarkerIndex) {
        nextMarkerIndex = index;
      }
    });

    const before = value.slice(0, markerIndex);
    const after = nextMarkerIndex < value.length ? value.slice(nextMarkerIndex) : '';
    const withSegment =
      before && !before.endsWith(' ') ? `${before} ${nextSegment}` : `${before}${nextSegment}`;
    const updatedValue =
      after && !withSegment.endsWith(' ') && !after.startsWith(' ')
        ? `${withSegment} ${after}`
        : `${withSegment}${after}`;

    onChange(updatedValue.trim());
  };

  return (
    <>
      <Flex vertical className={styles.element} gap={16}>
        <Flex gap={4} align="start" vertical>
          <Text variant="md" fontWeight={500}>
            {t('name')}
          </Text>

          <Text variant="sm" style={{ color: 'var(--color-text-tertiary)' }}>
            {t('nameDescription')}
          </Text>
        </Flex>

        <Form.Item
          name="name"
          help={errors.name && errors.name.message}
          validateStatus={errors.name ? 'error' : ''}
          className={styles.label}
        >
          <Controller
            control={control}
            name="name"
            render={({ field }) => (
              <Input
                {...field}
                id="name"
                placeholder={t('namePlaceholder')}
                className={styles.input}
                showCount
                disabled={isLoading}
              />
            )}
          />
        </Form.Item>
      </Flex>

      <Flex vertical className={styles.element} gap={16}>
        <Flex gap={4} align="start" vertical>
          <Text variant="md" fontWeight={500}>
            {t('role')}
          </Text>

          <Text variant="sm" style={{ color: 'var(--color-text-tertiary)' }}>
            {t('roleDescription')}
          </Text>
        </Flex>

        <Form.Item
          name="role"
          help={errors.role && errors.role.message}
          validateStatus={errors.role ? 'error' : ''}
          className={styles.label}
        >
          <Controller
            control={control}
            name="role"
            render={({ field }) => (
              <>
                <TextArea
                  {...field}
                  id="role"
                  placeholder={t('rolePlaceholder')}
                  className={styles.area}
                  style={{ resize: 'none' }}
                  showCount
                  autoSize={{ minRows: 3, maxRows: 8 }}
                  disabled={isLoading}
                />

                <Flex vertical gap={8} className={styles.prefillSection}>
                  {roleStep === 'position' && (
                    <Flex align="center" gap={8} wrap className={styles.prefillGroup}>
                      <span className={styles.prefillLabel}>
                        {t(ROLE_GROUP_LABEL_KEYS.position)}
                      </span>
                      {ROLE_POSITION_OPTIONS.map((option) => (
                        <button
                          key={option.value}
                          type="button"
                          className={classNames(styles.prefillButton, {
                            [styles.prefillButtonActive]: rolePreset.position === option.value,
                          })}
                          onClick={() => {
                            const nextPreset = {
                              position: option.value,
                              gender: undefined,
                              name: undefined,
                              goal: undefined,
                            };
                            setRolePreset(nextPreset);
                            applyPresetSegment(
                              buildRoleSegment('position', nextPreset),
                              getRoleMarker('position'),
                              getRoleMarkers(),
                              field.value ?? '',
                              field.onChange,
                            );
                          }}
                          disabled={isLoading}
                        >
                          {t(option.labelKey)}
                        </button>
                      ))}
                    </Flex>
                  )}

                  {roleStep === 'gender' && (
                    <Flex align="center" gap={8} wrap className={styles.prefillGroup}>
                      <span className={styles.prefillLabel}>{t(ROLE_GROUP_LABEL_KEYS.gender)}</span>
                      {ROLE_GENDER_OPTIONS.map((option) => (
                        <button
                          key={option.value}
                          type="button"
                          className={classNames(styles.prefillButton, {
                            [styles.prefillButtonActive]: rolePreset.gender === option.value,
                          })}
                          onClick={() => {
                            const nextPreset = {
                              ...rolePreset,
                              gender: option.value,
                              name: undefined,
                              goal: undefined,
                            };
                            setRolePreset(nextPreset);
                            applyPresetSegment(
                              buildRoleSegment('gender', nextPreset),
                              getRoleMarker('gender'),
                              getRoleMarkers(),
                              field.value ?? '',
                              field.onChange,
                            );
                          }}
                          disabled={isLoading}
                        >
                          {t(option.labelKey)}
                        </button>
                      ))}
                    </Flex>
                  )}

                  {roleStep === 'name' && (
                    <Flex align="center" gap={8} wrap className={styles.prefillGroup}>
                      <span className={styles.prefillLabel}>{t(ROLE_GROUP_LABEL_KEYS.name)}</span>
                      {getRoleNameOptions(rolePreset.gender).map((name) => (
                        <button
                          key={name}
                          type="button"
                          className={classNames(styles.prefillButton, {
                            [styles.prefillButtonActive]: rolePreset.name === name,
                          })}
                          onClick={() => {
                            const nextPreset = { ...rolePreset, name };
                            setRolePreset(nextPreset);
                            applyPresetSegment(
                              buildRoleSegment('name', nextPreset),
                              getRoleMarker('name'),
                              getRoleMarkers(),
                              field.value ?? '',
                              field.onChange,
                            );
                          }}
                          disabled={isLoading}
                        >
                          {name}
                        </button>
                      ))}
                    </Flex>
                  )}

                  {roleStep === 'goal' && (
                    <Flex align="center" gap={8} wrap className={styles.prefillGroup}>
                      <span className={styles.prefillLabel}>{t(ROLE_GROUP_LABEL_KEYS.goal)}</span>
                      {ROLE_GOAL_OPTIONS.map((goal) => (
                        <button
                          key={goal.value}
                          type="button"
                          className={classNames(styles.prefillButton, {
                            [styles.prefillButtonActive]: rolePreset.goal === goal.value,
                          })}
                          onClick={() => {
                            const nextPreset = { ...rolePreset, goal: goal.value };
                            setRolePreset(nextPreset);
                            applyPresetSegment(
                              buildRoleSegment('goal', nextPreset),
                              getRoleMarker('goal'),
                              getRoleMarkers(),
                              field.value ?? '',
                              field.onChange,
                            );
                          }}
                          disabled={isLoading}
                        >
                          {t(goal.labelKey)}
                        </button>
                      ))}
                    </Flex>
                  )}
                </Flex>
              </>
            )}
          />
        </Form.Item>
      </Flex>

      <Flex vertical className={styles.element} gap={12}>
        <Flex gap={4} align="start" vertical>
          <Text variant="md" fontWeight={500}>
            {t('style')}
          </Text>

          <Text variant="sm" style={{ color: 'var(--color-text-tertiary)' }}>
            {t('styleTooltip')}
          </Text>
        </Flex>

        <Form.Item
          name="style"
          help={errors.style && errors.style.message}
          validateStatus={errors.style ? 'error' : ''}
          className={styles.label}
        >
          <Controller
            control={control}
            name="style"
            render={({ field }) => (
              <>
                <TextArea
                  {...field}
                  id="style"
                  placeholder={t('stylePlaceholder')}
                  className={styles.area}
                  style={{ resize: 'none' }}
                  showCount
                  autoSize={{ minRows: 3, maxRows: 8 }}
                  disabled={isLoading}
                />

                <Flex vertical gap={8} className={styles.prefillSection}>
                  {styleStep === 'addressing' && (
                    <Flex align="center" gap={8} wrap className={styles.prefillGroup}>
                      <span className={styles.prefillLabel}>
                        {t(STYLE_GROUP_LABEL_KEYS.addressing)}
                      </span>
                      {STYLE_ADDRESSING_OPTIONS.map((option) => (
                        <button
                          key={option.value}
                          type="button"
                          className={classNames(styles.prefillButton, {
                            [styles.prefillButtonActive]: stylePreset.addressing === option.value,
                          })}
                          onClick={() => {
                            const nextPreset = { ...stylePreset, addressing: option.value };
                            setStylePreset(nextPreset);
                            applyPresetSegment(
                              buildStyleSegment('addressing', nextPreset),
                              getStyleMarker('addressing'),
                              getStyleMarkers(),
                              field.value ?? '',
                              field.onChange,
                            );
                          }}
                          disabled={isLoading}
                        >
                          {t(option.labelKey)}
                        </button>
                      ))}
                    </Flex>
                  )}

                  {styleStep === 'tone' && (
                    <Flex align="center" gap={8} wrap className={styles.prefillGroup}>
                      <span className={styles.prefillLabel}>{t(STYLE_GROUP_LABEL_KEYS.tone)}</span>
                      {STYLE_TONE_OPTIONS.map((option) => (
                        <button
                          key={option.value}
                          type="button"
                          className={classNames(styles.prefillButton, {
                            [styles.prefillButtonActive]: stylePreset.tone === option.value,
                          })}
                          onClick={() => {
                            const nextPreset = { ...stylePreset, tone: option.value };
                            setStylePreset(nextPreset);
                            applyPresetSegment(
                              buildStyleSegment('tone', nextPreset),
                              getStyleMarker('tone'),
                              getStyleMarkers(),
                              field.value ?? '',
                              field.onChange,
                            );
                          }}
                          disabled={isLoading}
                        >
                          {t(option.labelKey)}
                        </button>
                      ))}
                    </Flex>
                  )}

                  {styleStep === 'emojis' && (
                    <Flex align="center" gap={8} wrap className={styles.prefillGroup}>
                      <span className={styles.prefillLabel}>
                        {t(STYLE_GROUP_LABEL_KEYS.emojis)}
                      </span>
                      {STYLE_EMOJI_OPTIONS.map((option) => (
                        <button
                          key={option.value}
                          type="button"
                          className={classNames(styles.prefillButton, {
                            [styles.prefillButtonActive]: stylePreset.emojis === option.value,
                          })}
                          onClick={() => {
                            const nextPreset = { ...stylePreset, emojis: option.value };
                            setStylePreset(nextPreset);
                            applyPresetSegment(
                              buildStyleSegment('emojis', nextPreset),
                              getStyleMarker('emojis'),
                              getStyleMarkers(),
                              field.value ?? '',
                              field.onChange,
                            );
                          }}
                          disabled={isLoading}
                        >
                          {t(option.labelKey)}
                        </button>
                      ))}
                    </Flex>
                  )}

                  {styleStep === 'length' && (
                    <Flex align="center" gap={8} wrap className={styles.prefillGroup}>
                      <span className={styles.prefillLabel}>
                        {t(STYLE_GROUP_LABEL_KEYS.length)}
                      </span>
                      {STYLE_LENGTH_OPTIONS.map((option) => (
                        <button
                          key={option.value}
                          type="button"
                          className={classNames(styles.prefillButton, {
                            [styles.prefillButtonActive]: stylePreset.length === option.value,
                          })}
                          onClick={() => {
                            const nextPreset = { ...stylePreset, length: option.value };
                            setStylePreset(nextPreset);
                            applyPresetSegment(
                              buildStyleSegment('length', nextPreset),
                              getStyleMarker('length'),
                              getStyleMarkers(),
                              field.value ?? '',
                              field.onChange,
                            );
                          }}
                          disabled={isLoading}
                        >
                          {t(option.labelKey)}
                        </button>
                      ))}
                    </Flex>
                  )}
                </Flex>
              </>
            )}
          />
        </Form.Item>
      </Flex>

      <Flex vertical className={styles.element} gap={12}>
        <Flex gap={4} align="start" vertical>
          <Text variant="md" fontWeight={500}>
            {t('chargeMethod.title')}
          </Text>

          <Text
            variant="sm"
            style={{ color: 'var(--color-text-tertiary)', whiteSpace: 'pre-line' }}
          >
            {t('chargeMethod.description')}
          </Text>
        </Flex>

        <Form.Item
          help={errors.charge_method && errors.charge_method.message}
          validateStatus={errors.charge_method ? 'error' : ''}
          className={styles.label}
        >
          <Controller
            control={control}
            name="charge_method"
            render={({ field }) => (
              <Radio.Group
                className={styles.radioGroup}
                value={field.value}
                options={[
                  { label: t('chargeMethod.lead'), value: 'lead' },
                  { label: t('chargeMethod.chat'), value: 'chat' },
                ]}
                onChange={(event) => field.onChange(event.target.value)}
                optionType="button"
                size="large"
                block
                disabled={isLoading}
              />
            )}
          />
        </Form.Item>
      </Flex>
    </>
  );
};
